import React, {createContext, useCallback, useContext, useState} from 'react'
import SuccessDialog from 'lib/ui/Dialog/SuccessDialog'
import ErrorDialog from 'lib/ui/Dialog/ErrorDialog'
import {TeamMember} from 'auth/user'
import {useObvioAuth} from 'obvio/auth'

type PastDueRecoveryContextValue = {
  updateCardFormOpen: boolean
  openUpdateCardForm: () => void
  closeUpdateCardForm: () => void
  handleRecoverSuccess: (updatedUser: TeamMember) => void
  handleRecoverError: (message: string) => void
}

const PastDueRecoveryContext = createContext<
  PastDueRecoveryContextValue | undefined
>(undefined)

export function PastDueRecoveryProvider(props: {children: React.ReactNode}) {
  const {setUser} = useObvioAuth()
  const [updateCardFormOpen, setUpdateCardFormOpen] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [pendingUser, setPendingUser] = useState<TeamMember | null>(null)

  const openUpdateCardForm = useCallback(() => {
    setUpdateCardFormOpen(true)

    requestAnimationFrame(() => {
      document
        .getElementById('default-credit-card')
        ?.scrollIntoView?.({behavior: 'smooth', block: 'start'})
    })
  }, [])

  const closeUpdateCardForm = useCallback(() => {
    setUpdateCardFormOpen(false)
  }, [])

  const handleRecoverSuccess = useCallback((updatedUser: TeamMember) => {
    setPendingUser(updatedUser)
    setUpdateCardFormOpen(false)
    setSuccessMessage(
      'Payment successful! Your new card is now the default payment method.',
    )
  }, [])

  const handleRecoverError = useCallback((message: string) => {
    setErrorMessage(message)
  }, [])

  const handleCloseSuccessModal = () => {
    setSuccessMessage(null)

    if (pendingUser) {
      setUser(pendingUser)
      setPendingUser(null)
    }
  }

  return (
    <PastDueRecoveryContext.Provider
      value={{
        updateCardFormOpen,
        openUpdateCardForm,
        closeUpdateCardForm,
        handleRecoverSuccess,
        handleRecoverError,
      }}
    >
      <SuccessDialog
        showing={Boolean(successMessage)}
        onClose={handleCloseSuccessModal}
      >
        {successMessage ?? ''}
      </SuccessDialog>
      <ErrorDialog
        showing={Boolean(errorMessage)}
        onClose={() => setErrorMessage(null)}
      >
        {errorMessage ?? ''}
      </ErrorDialog>
      {props.children}
    </PastDueRecoveryContext.Provider>
  )
}

export function usePastDueRecovery() {
  const context = useContext(PastDueRecoveryContext)

  if (context === undefined) {
    throw new Error(
      'usePastDueRecovery must be used within a PastDueRecoveryProvider',
    )
  }

  return context
}

export function useOptionalPastDueRecovery() {
  return useContext(PastDueRecoveryContext)
}
